import React from "react";
import dynamic from "next/dynamic";

import getRegionComplianceRules from "@/api/requests/consumerApi/getRegionComplianceRules";
import { getDispensary } from "@/api/requests/getDispensary";
import getMenuItem from "@/api/requests/getMenuItem";
import getMenuItems, { GetMenuItemsReturn } from "@/api/requests/getMenuItems";
import getSimilarMenuItems from "@/api/requests/getSimilarMenuItems";
import { DISPENSARY_PAGES } from "@/constants/dispensary";
import { Dispensary } from "@/custom-types/Dispensary";
import { MenuItem } from "@/custom-types/MenuItem";
import { retailerPdpMetaData } from "@/lib/retailerMetadata";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import isDispensaryDualLicense from "@/utils/dispensary/isDispensaryDualLicense";
import generateDispensaryRedirect from "@/utils/generateDispensaryRedirect";
import { getMenuTypeCookie } from "@/utils/menuTypeUtils";
import { throwOnRedirectHint } from "@/utils/redirectHints";
import { getCountryCodeHeader } from "@/utils/requestContext";

import DispensaryHeader from "@/components/Dispensary/DispensaryHeader";
import DispensaryTabs from "@/components/Dispensary/DispensaryTabs";
import MenuItemPage from "@/components/Dispensary/MenuItemPage";
import { AvailableFilter } from "@/components/DynamicFilters/types";
import MetaTags from "@/components/MetaTags";

const MenuItem404Page = dynamic(
  () => import("@/components/Dispensary/MenuItemPage/404"),
);

type PdpPageProps = {
  dispensary: Dispensary;
  menuItem: MenuItem | null;
  menuData: GetMenuItemsReturn;
  similarMenuItems: MenuItem[];
};

const PdpPage: React.FC<PdpPageProps> = ({
  dispensary,
  menuItem,
  menuData,
  similarMenuItems,
}) => {
  if (!menuItem) {
    return <MenuItem404Page dispensary={dispensary} />;
  } else {
    const { coverPhotoUrl, logoUrl, name } = dispensary;

    //used for all menu item pages (dispensary, CA, cbd)
    const { description, image, title } = retailerPdpMetaData(
      coverPhotoUrl,
      menuItem.brandName,
      logoUrl,
      menuItem.name,
      name,
    );

    return (
      <>
        <MetaTags
          description={description}
          image={image}
          robots="noindex,noarchive"
          shouldShowAlternates={false}
          title={title}
        />
        <DispensaryHeader dispensary={dispensary} />
        <section>
          <DispensaryTabs
            activeTab={DISPENSARY_PAGES.menu}
            dispensary={dispensary}
          />
          <MenuItemPage
            dispensary={dispensary}
            menuItem={menuItem}
            similarMenuItems={similarMenuItems}
            availableFilters={menuData.availableFilters as AvailableFilter[]}
          />
        </section>
      </>
    );
  }
};

export const getServerSideProps = createGetServerSideProps<PdpPageProps>(
  async (context) => {
    const { query, req, resolvedUrl } = context;
    const { slug: dispensarySlug, menuItem: menuItemId } = query;

    const dispensary = await throwOnRedirectHint(
      () => getDispensary(dispensarySlug as string),
      dispensarySlug as string,
    );

    const domainCountryCode = getCountryCodeHeader(req.headers);

    const redirect = generateDispensaryRedirect(
      domainCountryCode,
      resolvedUrl,
      query,
      dispensary,
    );

    if (redirect) {
      return { redirect };
    }

    // fallback if redirect not generated
    if (!dispensary) {
      return {
        notFound: true,
      };
    }

    const menuTypeCookie = getMenuTypeCookie(req);
    const prefersMedical = menuTypeCookie === "Med";
    const isDualLicenseDispensary = isDispensaryDualLicense(
      dispensary.tags || [],
    );

    const getMenuItemCall = getMenuItem(parseInt(menuItemId as string, 10));

    const getMenuItemsCall = getMenuItems(dispensarySlug as string, {
      perPage: 0,
      ...(isDualLicenseDispensary && {
        menu_type: prefersMedical ? "Med" : "Rec",
      }),
    });
    const getSimilarItemsCall = getSimilarMenuItems(menuItemId as string, {
      dispensarySlug: dispensarySlug as string,
      ...(isDualLicenseDispensary && {
        medical: `${prefersMedical}`,
      }),
    });

    const getComplianceRulesCall = getRegionComplianceRules(
      dispensary.state,
      dispensary.country,
    );

    const [menuItem, menuData, similarMenuItems, complianceRuleset] =
      await Promise.all([
        getMenuItemCall,
        getMenuItemsCall,
        getSimilarItemsCall,
        getComplianceRulesCall,
      ]);

    return {
      props: {
        complianceRuleset,
        dispensary,
        menuData,
        menuItem: menuItem || null,
        similarMenuItems,
      },
    };
  },
);

export default PdpPage;
